/** SUNTRONIX ONE — canonical primary navigation model (Feb 2026).
 *
 *  Six primary destinations, always visible.  Each primary owns a set of
 *  routes as its canonical parent.  Sub-navigation appears inline underneath
 *  the ACTIVE primary only.  MORE spills over into a grouped popover so it
 *  never becomes a scrolling list.
 *
 *  Rules:
 *    - Every existing route must be reachable through exactly ONE primary.
 *    - Role-based visibility filtering is applied to sub-nav items.
 *    - The primary bar itself must NEVER require scrolling.
 */
import {
  Home, ListTodo, Building2, Package, Wrench, MoreHorizontal,
  Sparkles, BarChart3, LifeBuoy, Ticket, ShieldCheck,
} from "lucide-react";
import { tenantifyNav } from "@/lib/terminology";

/* -------------------------------------------------------------------------- */
/*  SUNTRONIX (internal) — HOME · WORK · CUSTOMERS · TECHNOLOGY · OPERATIONS · MORE */
/* -------------------------------------------------------------------------- */

/** Six primary destinations for Suntronix internal users. */
export const PRIMARY_SUNTRONIX = [
  { id: "home",       label: "Home",        icon: Home,     to: "/",          testId: "primary-home" },
  { id: "work",       label: "Work",        icon: ListTodo, to: "/work",      testId: "primary-work" },
  { id: "customers",  label: "Customers",   icon: Building2, to: "/companies", testId: "primary-customers" },
  { id: "technology", label: "Technology", icon: Package,  to: "/assets",    testId: "primary-technology" },
  { id: "operations", label: "Operations", icon: Wrench,   to: "/warehouse", testId: "primary-operations" },
  { id: "more",       label: "More",       icon: MoreHorizontal, to: null,   testId: "primary-more" },
];

/** Sub-navigation revealed under the ACTIVE primary section. */
export const SUB_NAV_SUNTRONIX = {
  home: [
    { to: "/",              label: "Overview",       testId: "sub-home-overview", end: true },
    { to: "/executive",     label: "Executive view", testId: "sub-home-executive" },
    { to: "/control-tower", label: "Control Tower",  testId: "sub-home-control-tower" },
    { to: "/concierge",     label: "Concierge",      testId: "sub-home-concierge" },
    { to: "/my-devices",    label: "My devices",     testId: "sub-home-my-devices" },
    { to: "/files",         label: "ONE Files",      testId: "sub-home-one-files" },
  ],
  work: [
    { to: "/work",       label: "All work",   testId: "sub-work-all" },
    { to: "/requests",   label: "Requests",   testId: "sub-work-requests" },
    { to: "/approvals",  label: "Approvals",  testId: "sub-work-approvals" },
    { to: "/exceptions", label: "Exceptions", testId: "sub-work-exceptions" },
    { to: "/mentions",   label: "@ Mentions", testId: "sub-work-mentions" },
    { to: "/intake",     label: "ONE Intake", testId: "sub-work-intake" },
  ],
  customers: [
    { to: "/companies",         label: "Companies",         testId: "sub-cust-companies" },
    { to: "/trade-partners",    label: "Partner List",      testId: "sub-cust-partners" },
    { to: "/employees",         label: "Employees",         testId: "sub-cust-employees" },
    { to: "/admin/sites",       label: "Customer sites",    testId: "sub-cust-sites" },
    { to: "/catalogue",         label: "Product catalogue", testId: "sub-cust-catalogue" },
    { to: "/services",          label: "Service catalogue", testId: "sub-cust-services" },
    { to: "/governance-centre", label: "Governance & QBR",  testId: "sub-cust-governance" },
  ],
  technology: [
    { to: "/technology/product-master", label: "Product Master", testId: "sub-tech-product-master" },
    { to: "/technology/product-intelligence", label: "Product Intelligence", testId: "sub-tech-product-intelligence" },
    { to: "/assets",       label: "Assets",            testId: "sub-tech-assets" },
    { to: "/inventory",    label: "My inventory",      testId: "sub-tech-inventory-cockpit" },
    { to: "/warranty",     label: "Warranty",          testId: "sub-tech-warranty" },
    { to: "/reservations", label: "Reservations",      testId: "sub-tech-reservations" },
    { to: "/loaners",      label: "Loaner pool",       testId: "sub-tech-loaners" },
    { to: "/rma",          label: "RMA",               testId: "sub-tech-rma" },
    { to: "/data-quality", label: "Data quality",      testId: "sub-tech-dq" },
    { to: "/discovery",    label: "Discovery",         testId: "sub-tech-discovery" },
    { to: "/connectors",   label: "Connectors",        testId: "sub-tech-connectors" },
  ],
  operations: [
    { to: "/deploy",       label: "Deployments",   testId: "sub-ops-deploy" },
    { to: "/offboarding",  label: "Recoveries",    testId: "sub-ops-recover" },
    { to: "/pickups",      label: "Reverse pickups", testId: "sub-ops-pickups" },
    { to: "/shipments",    label: "Shipments",     testId: "sub-ops-shipments" },
    { to: "/warehouse",    label: "Warehouse",     testId: "sub-ops-warehouse" },
    { to: "/inspection",   label: "Inspection",    testId: "sub-ops-inspection" },
    { to: "/service",      label: "Service",       testId: "sub-ops-service" },
    { to: "/data-erasure", label: "Data erasure",  testId: "sub-ops-erasure" },
    { to: "/itad",         label: "ITAD & Recycle", testId: "sub-ops-itad" },
    { to: "/partners",     label: "Partner network", testId: "sub-ops-partners" },
    { to: "/mobile",       label: "ONE Mobile",    testId: "sub-ops-mobile" },
  ],
};

/** MORE — grouped popover; never a scrolling list.  Role filtering still applies. */
export const MORE_SUNTRONIX = [
  { section: "ONE Workspace", items: [
    { to: "/files",                          label: "ONE Files",          testId: "more-one-files" },
    { to: "/admin/documents/authorised-signers", label: "Authorised Signers", testId: "more-authorised-signers" },
  ]},
  { section: "Insights", items: [
    { to: "/reports",          label: "Reports",         testId: "more-reports" },
    { to: "/scenario-lab",     label: "Scenario Lab",    testId: "more-scenario-lab" },
    { to: "/economics",        label: "Economics",       testId: "more-economics" },
    { to: "/circularity",      label: "Circularity",     testId: "more-circularity" },
    { to: "/refresh-engine",   label: "Refresh engine",  testId: "more-refresh-engine" },
    { to: "/refresh-forecast", label: "Refresh forecast",testId: "more-refresh-forecast" },
    { to: "/benchmarks",       label: "Benchmarks",      testId: "more-benchmarks" },
  ]},
  { section: "Trust", items: [
    { to: "/security",     label: "Cyber intelligence",    testId: "more-security" },
    { to: "/trust",        label: "Continuous compliance", testId: "more-trust" },
    { to: "/audit",        label: "Audit trail",           testId: "more-audit" },
    { to: "/readiness",    label: "Security readiness",    testId: "more-readiness" },
  ]},
  { section: "Marketplace & Knowledge", items: [
    { to: "/marketplace",     label: "Marketplace",       testId: "more-marketplace" },
    { to: "/knowledge",       label: "SOP & Knowledge",   testId: "more-knowledge" },
    { to: "/automation",      label: "Automation Centre", testId: "more-automation" },
    { to: "/implementation",  label: "Implementation",    testId: "more-implementation" },
    { to: "/conversations",   label: "Conversations",     testId: "more-conversations" },
  ]},
  { section: "Administration", items: [
    { to: "/admin/control-centre", label: "Control centre",     testId: "more-admin-centre" },
    { to: "/admin/onboard-tenant", label: "Onboard tenant",     testId: "more-admin-onboard-tenant" },
    { to: "/admin/legal",          label: "Legal agreements",   testId: "more-admin-legal" },
    { to: "/admin/data-quality",   label: "Data Quality Control", testId: "more-admin-data-quality" },
    { to: "/admin/catalogue-masters", label: "Category & Brand masters", testId: "more-admin-catalogue-masters" },
    { to: "/admin/catalogue-cleanup", label: "Catalogue Cleanup AI", testId: "more-admin-catalogue-cleanup" },
    { to: "/admin/marketplace",    label: "Marketplace admin",  testId: "more-admin-marketplace" },
    { to: "/admin/studio",         label: "Platform studio",    testId: "more-studio" },
    { to: "/users",                label: "Users & roles",      testId: "more-users" },
    { to: "/location-master",      label: "Location master",    testId: "more-location-master" },
    { to: "/admin/couriers",       label: "Courier partners",   testId: "more-couriers" },
    { to: "/imports",              label: "Import centre",      testId: "more-imports" },
  ]},
  { section: "Commercial & Finance", items: [
    { to: "/commercial",                label: "Commercial console",    testId: "more-commercial" },
    { to: "/commercial/contracts",      label: "Contract centre",       testId: "more-contract-centre" },
    { to: "/commercial/billing",        label: "Monthly billing",       testId: "more-billing-centre" },
    { to: "/commercial/billing/shadow", label: "Shadow billing",        testId: "more-shadow-billing" },
    { to: "/commercial/profitability",  label: "Profitability & Leakage", testId: "more-profitability" },
    { to: "/commercial/buybacks",       label: "Buyback credit",          testId: "more-buybacks" },
    { to: "/operations/office-batches", label: "Office deploy batches",   testId: "more-office-batches" },
    { to: "/renewals",                  label: "Renewal alerts",          testId: "more-renewals" },
  ]},
  { section: "Suntronix HQ", items: [
    { to: "/governance",  label: "Release console",  testId: "more-governance" },
    { to: "/pilot",       label: "Pilot programme",  testId: "more-pilot" },
    { to: "/scale",       label: "Scale & resilience", testId: "more-scale" },
    { to: "/launch",      label: "Launch readiness", testId: "more-launch" },
    { to: "/preview/navigation", label: "Navigation preview", testId: "more-nav-preview" },
    { to: "/whats-new",   label: "What's new",       testId: "more-whats-new" },
    { to: "/notices",     label: "Advance notices",  testId: "more-notices" },
    { to: "/feedback",    label: "Feedback",         testId: "more-feedback" },
  ]},
];

/* -------------------------------------------------------------------------- */
/*  CUSTOMER — HOME · WORK · TECHNOLOGY · SERVICES · INSIGHTS · MORE          */
/* -------------------------------------------------------------------------- */

export const PRIMARY_CUSTOMER = tenantifyNav([
  { id: "home",       label: "Home",       icon: Home,     to: "/",         testId: "primary-home" },
  { id: "work",       label: "Work",       icon: ListTodo, to: "/requests", testId: "primary-work" },
  { id: "technology", label: "Technology", icon: Package,  to: "/assets",   testId: "primary-technology" },
  { id: "services",   label: "Services",   icon: LifeBuoy, to: "/services", testId: "primary-services" },
  { id: "insights",   label: "Insights",   icon: BarChart3, to: "/reports", testId: "primary-insights" },
  { id: "more",       label: "More",       icon: MoreHorizontal, to: null,  testId: "primary-more" },
]);

export const SUB_NAV_CUSTOMER = tenantifyNav({
  home: [
    { to: "/",           label: "Overview",  testId: "sub-home-overview", end: true },
    { to: "/concierge",  label: "Concierge", testId: "sub-home-concierge" },
    { to: "/my-devices", label: "My devices", testId: "sub-home-my-devices" },
  ],
  work: [
    { to: "/requests",   label: "Requests",  testId: "sub-work-requests" },
    { to: "/approvals",  label: "Approvals", testId: "sub-work-approvals" },
    { to: "/exceptions", label: "Exceptions", testId: "sub-work-exceptions" },
    { to: "/mentions",   label: "@ Mentions", testId: "sub-work-mentions" },
  ],
  technology: [
    { to: "/assets",       label: "Assets",           testId: "sub-tech-assets" },
    { to: "/inventory",    label: "My inventory",     testId: "sub-tech-inventory-cockpit" },
    { to: "/employees",    label: "Employees",        testId: "sub-tech-employees" },
    { to: "/warehouse",    label: "Inventory",        testId: "sub-tech-inventory" },
    { to: "/catalogue",    label: "Catalogue",        testId: "sub-tech-catalogue" },
    { to: "/warranty",     label: "Warranty",         testId: "sub-tech-warranty" },
    { to: "/data-quality", label: "Data quality",     testId: "sub-tech-dq" },
    { to: "/connectors",   label: "Data connectors",  testId: "sub-tech-connectors" },
  ],
  services: [
    { to: "/services",    label: "Service catalogue", testId: "sub-svc-catalogue" },
    { to: "/deploy",      label: "Deploy device",     testId: "sub-svc-deploy" },
    { to: "/pickups",     label: "Recover device",    testId: "sub-svc-recover" },
    { to: "/offboarding", label: "Offboard employee", testId: "sub-svc-offboard" },
  ],
  insights: [
    { to: "/reports",           label: "Reports",           testId: "sub-ins-reports" },
    { to: "/executive",         label: "Executive view",    testId: "sub-ins-executive" },
    { to: "/governance-centre", label: "Governance & QBR",  testId: "sub-ins-governance" },
    { to: "/refresh-engine",    label: "Refresh planner",   testId: "sub-ins-refresh" },
    { to: "/refresh-forecast",  label: "Refresh forecast",  testId: "sub-ins-forecast" },
    { to: "/circularity",       label: "Circularity",       testId: "sub-ins-circularity" },
    { to: "/economics",         label: "Economics",         testId: "sub-ins-economics" },
    { to: "/benchmarks",        label: "Benchmarks",        testId: "sub-ins-benchmarks" },
  ],
});

export const MORE_CUSTOMER = tenantifyNav([
  { section: "Change experience", items: [
    { to: "/whats-new", label: "What's new",      testId: "more-whats-new" },
    { to: "/notices",   label: "Advance notices", testId: "more-notices" },
    { to: "/feedback",  label: "Feedback",        testId: "more-feedback" },
  ]},
  { section: "Records", items: [
    { to: "/marketplace",   label: "Marketplace",     testId: "more-marketplace" },
    { to: "/audit",         label: "Activity trail",  testId: "more-audit" },
    { to: "/conversations", label: "Conversations",   testId: "more-conversations" },
  ]},
  { section: "Administration", items: [
    { to: "/companies", label: "Companies",     testId: "more-companies" },
    { to: "/users",     label: "Users & roles", testId: "more-users" },
    { to: "/imports",   label: "Imports",       testId: "more-imports" },
    { to: "/admin/sites", label: "Sites",       testId: "more-sites" },
  ]},
]);

/* -------------------------------------------------------------------------- */
/*  Reverse index — pathname → primary id.  Used to auto-expand the correct   */
/*  sub-nav and to highlight the active primary tile as the user navigates.   */
/* -------------------------------------------------------------------------- */
function buildRouteIndex(subNav, more) {
  const idx = {};
  for (const [primaryId, items] of Object.entries(subNav)) {
    for (const it of items) idx[it.to] = primaryId;
  }
  for (const grp of more) {
    for (const it of grp.items) idx[it.to] = "more";
  }
  return idx;
}
const ROUTE_TO_PRIMARY_SUNTRONIX = buildRouteIndex(SUB_NAV_SUNTRONIX, MORE_SUNTRONIX);
const ROUTE_TO_PRIMARY_CUSTOMER  = buildRouteIndex(SUB_NAV_CUSTOMER,  MORE_CUSTOMER);

/** Return the primary section id (home/work/…/more) that owns the given path. */
export function primaryIdForPath(pathname, isCustomer) {
  const idx = isCustomer ? ROUTE_TO_PRIMARY_CUSTOMER : ROUTE_TO_PRIMARY_SUNTRONIX;
  if (idx[pathname]) return idx[pathname];
  // Prefix match for /services/detail/:code etc.
  for (const key of Object.keys(idx)) {
    if (key !== "/" && pathname.startsWith(key + "/")) return idx[key];
  }
  return "home";
}
